import { useState, useEffect, useCallback } from 'react';
import {
  CreditCard,
  Search,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  DollarSign,
  Printer,
  MessageCircle,
  History,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { GlassCard } from '@/components/ui/glass-card';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Badge } from '@/components/ui/badge';
import { apiFetch } from '@/lib/api';
import { useAuth } from '@/lib/auth';

// ─── TYPES ───────────────────────────────────────────────────────────

interface Debt {
  id: string;
  customer_id: string;
  customer_name: string;
  customer_cpf: string;
  customer_phone: string;
  amount: number;
  purchase_date: string;
  due_date: string;
  status: 'em_dia' | 'vencido' | 'pago';
  days_overdue?: number;
  sale_id?: string;
}

interface DebtListResponse {
  data: Debt[];
  pagination: { total: number; page: number; limit: number; totalPages: number };
}

interface OverdueResponse {
  data: Debt[];
  summary: { total_debts: number; total_amount: number; total_customers: number };
}

interface Customer {
  id: string;
  full_name: string;
  cpf: string;
  phone: string;
  credit_limit: number | null;
  outstanding_balance: number;
  available_credit: number | null;
}

interface CustomerCreditResponse {
  customer: Customer;
  debts: Debt[];
  overdue_debts: Debt[];
  pre_overdue_debts: Debt[];
  summary: {
    total_debts: number;
    total_amount: number;
    overdue_count: number;
    overdue_amount: number;
  };
}

interface PaymentResult {
  message: string;
  amount_paid: number;
  new_balance: number;
  debts_affected: Array<{ id: string; amount_paid: number; fully_paid: boolean }>;
}

interface ReceiptData {
  receipt: {
    id: string;
    receipt_number: number;
    tenant_name?: string;
    date: string;
    time: string;
    customer_name: string;
    customer_cpf?: string;
    amount_paid: number;
    previous_balance: number;
    remaining_balance: number;
    is_full_payment: boolean;
    operator_name: string;
  };
}

type CreditTab = 'debitos' | 'receber' | 'vencidos' | 'historico';

// ─── HELPERS ─────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  // Handle YYYY-MM-DD format (add T00:00:00 to avoid timezone issues)
  let d: Date;
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    d = new Date(year, month - 1, day);
  } else {
    d = new Date(dateStr);
  }
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('pt-BR');
}

function calcDaysOverdue(dueDate: string): number {
  const due = new Date(dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  const diff = Math.floor((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

function printReceipt(receipt: ReceiptData['receipt'], tenantName: string) {
  const isFullPayment = receipt.remaining_balance <= 0;
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Recibo de Pagamento #${receipt.receipt_number}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Courier New', monospace;
      width: 280px;
      margin: 0 auto;
      padding: 10px;
      font-size: 12px;
      line-height: 1.4;
    }
    .center { text-align: center; }
    .bold { font-weight: bold; }
    .separator { border-top: 1px dashed #000; margin: 8px 0; }
    .row { display: flex; justify-content: space-between; }
    .highlight { font-size: 14px; font-weight: bold; }
    .full-payment { 
      border: 2px solid #000; 
      padding: 4px 8px; 
      margin: 8px 0; 
      text-align: center; 
      font-weight: bold;
      font-size: 14px;
    }
    @media print {
      body { width: 100%; }
    }
  </style>
</head>
<body>
  <div class="center bold" style="font-size:14px;">${tenantName || 'PEG E PAG'}</div>
  <div class="separator"></div>
  <div class="center bold highlight">RECIBO DE PAGAMENTO #${receipt.receipt_number}</div>
  <div class="center">${receipt.date} ${receipt.time}</div>
  <div class="separator"></div>
  <div><strong>Cliente:</strong> ${receipt.customer_name}</div>
  ${receipt.customer_cpf ? `<div><strong>CPF:</strong> ${receipt.customer_cpf}</div>` : ''}
  <div class="separator"></div>
  <div class="row"><span>Saldo Anterior:</span><span>${formatCurrency(receipt.previous_balance)}</span></div>
  <div class="row bold"><span>Valor Pago:</span><span>${formatCurrency(receipt.amount_paid)}</span></div>
  <div class="row"><span>Saldo Restante:</span><span>${formatCurrency(receipt.remaining_balance)}</span></div>
  ${isFullPayment ? '<div class="full-payment">*** QUITAÇÃO TOTAL ***</div>' : ''}
  <div class="separator"></div>
  <div>Operador: ${receipt.operator_name}</div>
  <div class="separator"></div>
  <div class="center" style="margin-top:10px;">Obrigado pelo pagamento!</div>
  <script>window.onload = function() { window.print(); }</script>
</body>
</html>`;

  const printWindow = window.open('', '_blank', 'width=320,height=600');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
  }
}

// ─── TAB 1: DÉBITOS ──────────────────────────────────────────────────

function DebitosTab() {
  const [debts, setDebts] = useState<Debt[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [summary, setSummary] = useState({
    totalReceivable: 0,
    totalOverdue: 0,
    totalOnTime: 0,
    totalCustomers: 0,
  });

  const fetchDebts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '100');
      if (filter !== 'all') params.set('status', filter);

      const res = await apiFetch(`/api/credit/debts?${params.toString()}`);
      if (!res.ok) throw new Error('Erro ao carregar débitos');
      const data: DebtListResponse = await res.json();
      setDebts(data.data);

      // Calculate summary from all debts (unfiltered)
      const allRes = await apiFetch('/api/credit/debts?limit=1000');
      if (allRes.ok) {
        const allData: DebtListResponse = await allRes.json();
        const all = allData.data;
        const receivable = all
          .filter((d) => d.status !== 'pago')
          .reduce((s, d) => s + Number(d.amount), 0);
        const overdue = all
          .filter((d) => d.status === 'vencido')
          .reduce((s, d) => s + Number(d.amount), 0);
        const onTime = all
          .filter((d) => d.status === 'em_dia')
          .reduce((s, d) => s + Number(d.amount), 0);
        const customers = new Set(
          all.filter((d) => d.status !== 'pago').map((d) => d.customer_id)
        ).size;
        setSummary({
          totalReceivable: receivable,
          totalOverdue: overdue,
          totalOnTime: onTime,
          totalCustomers: customers,
        });
      }
    } catch (err) {
      toast.error('Erro ao carregar débitos');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchDebts();
  }, [fetchDebts]);

  const filteredDebts = search
    ? debts.filter((d) =>
        d.customer_name?.toLowerCase().includes(search.toLowerCase())
      )
    : debts;

  const columns: Column<Debt>[] = [
    { key: 'customer_name', header: 'Cliente', sortable: true },
    {
      key: 'amount',
      header: 'Valor',
      sortable: true,
      render: (row) => <span className="font-medium">{formatCurrency(Number(row.amount))}</span>,
    },
    {
      key: 'purchase_date',
      header: 'Data Compra',
      sortable: true,
      render: (row) => formatDate(row.purchase_date),
    },
    {
      key: 'due_date',
      header: 'Vencimento',
      sortable: true,
      render: (row) => formatDate(row.due_date),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => {
        if (row.status === 'pago')
          return <Badge variant="muted">Pago</Badge>;
        if (row.status === 'vencido')
          return <Badge variant="destructive" dot>Vencido</Badge>;
        return <Badge variant="success" dot>Em dia</Badge>;
      },
    },
    {
      key: 'days_overdue',
      header: 'Dias em Atraso',
      render: (row) => {
        const days = row.status === 'vencido' ? calcDaysOverdue(row.due_date) : 0;
        return days > 0 ? (
          <span className="text-red-400 font-medium">{days} dias</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/20 p-2">
              <DollarSign className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total a Receber</p>
              <p className="text-lg font-bold text-foreground">
                {formatCurrency(summary.totalReceivable)}
              </p>
            </div>
          </div>
        </GlassCard>
        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-red-500/20 p-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Vencido</p>
              <p className="text-lg font-bold text-red-400">
                {formatCurrency(summary.totalOverdue)}
              </p>
            </div>
          </div>
        </GlassCard>
        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-green-500/20 p-2">
              <CheckCircle className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total em Dia</p>
              <p className="text-lg font-bold text-green-400">
                {formatCurrency(summary.totalOnTime)}
              </p>
            </div>
          </div>
        </GlassCard>
        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-cyan-500/20 p-2">
              <Users className="h-5 w-5 text-cyan-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Clientes Devedores</p>
              <p className="text-lg font-bold text-foreground">{summary.totalCustomers}</p>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex gap-2">
          {[
            { id: 'all', label: 'Todos' },
            { id: 'em_dia', label: 'Em Dia' },
            { id: 'vencido', label: 'Vencidos' },
            { id: 'pago', label: 'Pagos' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`rounded-lg px-3 py-1.5 text-sm transition-colors ${
                filter === f.id
                  ? 'bg-primary text-white'
                  : 'bg-card text-muted-foreground hover:bg-accent'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por cliente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-border bg-card pl-9 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={filteredDebts}
        getRowKey={(row) => row.id}
        emptyMessage="Nenhum débito encontrado"
      />
    </div>
  );
}


// ─── TAB 2: RECEBER PAGAMENTO ────────────────────────────────────────

interface ReceberPagamentoTabProps {
  preSelectedCustomerId?: string;
}

function ReceberPagamentoTab({ preSelectedCustomerId }: ReceberPagamentoTabProps) {
  const { tenant } = useAuth();
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerDebts, setCustomerDebts] = useState<Debt[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingCredit, setLoadingCredit] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Payment form
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState<string>('dinheiro');
  const [selectedDebtId, setSelectedDebtId] = useState<string>('');
  const [payMode, setPayMode] = useState<'fifo' | 'specific'>('fifo');

  // Receipt
  const [receiptData, setReceiptData] = useState<ReceiptData['receipt'] | null>(null);
  const [showReceipt, setShowReceipt] = useState(false);

  // Pre-select customer
  useEffect(() => {
    if (preSelectedCustomerId) {
      loadCustomerCredit(preSelectedCustomerId);
    }
  }, [preSelectedCustomerId]);

  const searchCustomers = async (query: string) => {
    if (query.length < 2) {
      setCustomers([]);
      return;
    }
    setLoadingCustomers(true);
    try {
      const res = await apiFetch(`/api/customers?search=${encodeURIComponent(query)}`);
      if (!res.ok) throw new Error('Erro ao buscar clientes');
      const data = await res.json();
      setCustomers(data.data || data || []);
    } catch {
      toast.error('Erro ao buscar clientes');
    } finally {
      setLoadingCustomers(false);
    }
  };

  const loadCustomerCredit = async (customerId: string) => {
    setLoadingCredit(true);
    try {
      const res = await apiFetch(`/api/credit/customer/${customerId}`);
      if (!res.ok) throw new Error('Erro ao carregar crédito');
      const data: CustomerCreditResponse = await res.json();
      setSelectedCustomer(data.customer);
      setCustomerDebts(data.debts);
    } catch {
      toast.error('Erro ao carregar dados do cliente');
    } finally {
      setLoadingCredit(false);
    }
  };

  const handleSelectCustomer = (customer: Customer) => {
    setCustomers([]);
    setCustomerSearch('');
    loadCustomerCredit(customer.id);
  };

  const handlePayment = async () => {
    if (!selectedCustomer || !payAmount || Number(payAmount) <= 0) {
      toast.error('Informe um valor válido para pagamento');
      return;
    }

    setSubmitting(true);
    const previousBalance = selectedCustomer.outstanding_balance;

    try {
      const body: Record<string, unknown> = {
        customer_id: selectedCustomer.id,
        amount: Number(payAmount),
        payment_method: payMethod,
      };
      if (payMode === 'specific' && selectedDebtId) {
        body.debt_id = selectedDebtId;
      }

      const res = await apiFetch('/api/credit/pay', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Erro ao registrar pagamento');
      }

      const result = await res.json();
      
      // Show success with change info if applicable
      if (result.change && result.change > 0) {
        toast.success(
          `Pagamento de ${formatCurrency(result.amount_paid)} registrado! Troco: ${formatCurrency(result.change)} | Novo saldo: ${formatCurrency(result.new_balance)}`
        );
      } else {
        toast.success(
          `Pagamento de ${formatCurrency(result.amount_paid)} registrado! Novo saldo: ${formatCurrency(result.new_balance)}`
        );
      }

      // Generate receipt
      try {
        const receiptRes = await apiFetch('/api/receipts/fiado-payment', {
          method: 'POST',
          body: JSON.stringify({
            customer_id: selectedCustomer.id,
            amount_paid: result.amount_paid,
            previous_balance: previousBalance,
            remaining_balance: result.new_balance,
          }),
        });

        if (receiptRes.ok) {
          const receiptResult: ReceiptData = await receiptRes.json();
          setReceiptData(receiptResult.receipt);
          setShowReceipt(true);
        }
      } catch {
        // Receipt generation is non-critical
      }

      // Refresh customer data
      loadCustomerCredit(selectedCustomer.id);
      setPayAmount('');
      setSelectedDebtId('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao registrar pagamento');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerSearch.length >= 2) {
        searchCustomers(customerSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  return (
    <div className="space-y-6">
      {/* Customer search */}
      {!selectedCustomer && (
        <GlassCard>
          <h3 className="text-sm font-semibold text-foreground mb-4">Buscar Cliente</h3>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar por nome ou CPF..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              className="w-full rounded-lg border border-border bg-background pl-9 pr-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground"
            />
            {loadingCustomers && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>
          {customers.length > 0 && (
            <div className="mt-2 rounded-lg border border-border bg-card max-h-48 overflow-y-auto">
              {customers.map((c) => (
                <button
                  key={c.id}
                  onClick={() => handleSelectCustomer(c)}
                  className="w-full text-left px-4 py-2.5 hover:bg-accent transition-colors border-b border-border/50 last:border-0"
                >
                  <span className="text-sm font-medium text-foreground">{c.full_name}</span>
                  {c.cpf && (
                    <span className="ml-2 text-xs text-muted-foreground">CPF: {c.cpf}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </GlassCard>
      )}

      {loadingCredit && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      )}

      {/* Customer info + payment form */}
      {selectedCustomer && !loadingCredit && (
        <>
          {/* Customer info card */}
          <GlassCard>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground">Dados do Cliente</h3>
              <button
                onClick={() => {
                  setSelectedCustomer(null);
                  setCustomerDebts([]);
                  setShowReceipt(false);
                  setReceiptData(null);
                }}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Trocar cliente
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Nome</p>
                <p className="text-sm font-medium text-foreground">{selectedCustomer.full_name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">CPF</p>
                <p className="text-sm font-medium text-foreground">{selectedCustomer.cpf || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Telefone</p>
                <p className="text-sm font-medium text-foreground">{selectedCustomer.phone || '-'}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-border/50">
              <div>
                <p className="text-xs text-muted-foreground">Limite de Crédito</p>
                <p className="text-sm font-bold text-foreground">
                  {selectedCustomer.credit_limit !== null
                    ? formatCurrency(selectedCustomer.credit_limit)
                    : 'Ilimitado'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Crédito Disponível</p>
                <p className="text-sm font-bold text-green-400">
                  {selectedCustomer.available_credit !== null
                    ? formatCurrency(selectedCustomer.available_credit)
                    : 'Ilimitado'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Saldo Devedor</p>
                <p className="text-sm font-bold text-red-400">
                  {formatCurrency(selectedCustomer.outstanding_balance)}
                </p>
              </div>
            </div>
          </GlassCard>

          {/* Open debts */}
          {customerDebts.length > 0 && (
            <GlassCard>
              <h3 className="text-sm font-semibold text-foreground mb-4">Débitos em Aberto</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {customerDebts.map((debt) => (
                  <div
                    key={debt.id}
                    className={`flex items-center justify-between rounded-lg border px-3 py-2 ${
                      selectedDebtId === debt.id
                        ? 'border-primary bg-primary/5'
                        : 'border-border/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {payMode === 'specific' && (
                        <input
                          type="radio"
                          name="debt_select"
                          checked={selectedDebtId === debt.id}
                          onChange={() => setSelectedDebtId(debt.id)}
                          className="accent-primary"
                        />
                      )}
                      <div>
                        <p className="text-sm text-foreground font-medium">
                          {formatCurrency(Number(debt.amount))}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Venc: {formatDate(debt.due_date)}
                        </p>
                      </div>
                    </div>
                    <Badge
                      variant={debt.status === 'vencido' ? 'destructive' : 'success'}
                      dot
                    >
                      {debt.status === 'vencido' ? 'Vencido' : 'Em dia'}
                    </Badge>
                  </div>
                ))}
              </div>
            </GlassCard>
          )}

          {/* Payment form */}
          {selectedCustomer.outstanding_balance > 0 && (
            <GlassCard>
              <h3 className="text-sm font-semibold text-foreground mb-4">Registrar Pagamento</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">
                    Valor do Pagamento
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={selectedCustomer.outstanding_balance}
                    value={payAmount}
                    onChange={(e) => setPayAmount(e.target.value)}
                    placeholder="0,00"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground"
                  />
                  <button
                    onClick={() =>
                      setPayAmount(String(selectedCustomer.outstanding_balance))
                    }
                    className="mt-1 text-xs text-primary hover:underline"
                  >
                    Pagar total ({formatCurrency(selectedCustomer.outstanding_balance)})
                  </button>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">
                    Forma de Pagamento
                  </label>
                  <select
                    value={payMethod}
                    onChange={(e) => setPayMethod(e.target.value)}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground"
                  >
                    <option value="dinheiro">Dinheiro</option>
                    <option value="cartao_debito">Cartão Débito</option>
                    <option value="cartao_credito">Cartão Crédito</option>
                    <option value="pix">PIX</option>
                  </select>
                </div>
              </div>

              {/* Pay mode */}
              <div className="mt-4">
                <label className="text-xs text-muted-foreground mb-2 block">
                  Modo de Pagamento
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setPayMode('fifo');
                      setSelectedDebtId('');
                    }}
                    className={`rounded-lg px-3 py-1.5 text-sm transition-colors ${
                      payMode === 'fifo'
                        ? 'bg-primary text-white'
                        : 'bg-card text-muted-foreground hover:bg-accent border border-border'
                    }`}
                  >
                    Mais antigo primeiro (FIFO)
                  </button>
                  <button
                    onClick={() => setPayMode('specific')}
                    className={`rounded-lg px-3 py-1.5 text-sm transition-colors ${
                      payMode === 'specific'
                        ? 'bg-primary text-white'
                        : 'bg-card text-muted-foreground hover:bg-accent border border-border'
                    }`}
                  >
                    Débito específico
                  </button>
                </div>
              </div>

              <button
                onClick={handlePayment}
                disabled={submitting || !payAmount || Number(payAmount) <= 0}
                className="mt-6 w-full flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-3 text-sm font-medium text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )}
                Registrar Pagamento
              </button>
            </GlassCard>
          )}

          {selectedCustomer.outstanding_balance <= 0 && customerDebts.length === 0 && (
            <GlassCard className="text-center py-8">
              <CheckCircle className="h-10 w-10 text-green-400 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                Este cliente não possui débitos em aberto.
              </p>
            </GlassCard>
          )}

          {/* Receipt preview */}
          {showReceipt && receiptData && (
            <GlassCard>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-foreground">
                  Comprovante de Pagamento
                </h3>
                <button
                  onClick={() => printReceipt(receiptData, tenant?.name || '')}
                  className="flex items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm text-white hover:bg-primary/90 transition-colors"
                >
                  <Printer className="h-4 w-4" />
                  Imprimir Comprovante
                </button>
              </div>
              <div className="rounded-lg bg-background border border-border p-4 font-mono text-xs space-y-1 max-w-[300px]">
                <p className="text-center font-bold">{tenant?.name || 'PEG E PAG'}</p>
                <p className="border-t border-dashed border-border my-2" />
                <p className="text-center font-bold">
                  RECIBO DE PAGAMENTO #{receiptData.receipt_number}
                </p>
                <p className="text-center text-muted-foreground">
                  {receiptData.date} {receiptData.time}
                </p>
                <p className="border-t border-dashed border-border my-2" />
                <p>Cliente: {receiptData.customer_name}</p>
                {receiptData.customer_cpf && <p>CPF: {receiptData.customer_cpf}</p>}
                <p className="border-t border-dashed border-border my-2" />
                <div className="flex justify-between">
                  <span>Saldo Anterior:</span>
                  <span>{formatCurrency(receiptData.previous_balance)}</span>
                </div>
                <div className="flex justify-between font-bold">
                  <span>Valor Pago:</span>
                  <span>{formatCurrency(receiptData.amount_paid)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Saldo Restante:</span>
                  <span>{formatCurrency(receiptData.remaining_balance)}</span>
                </div>
                {receiptData.is_full_payment && (
                  <p className="text-center font-bold mt-2 border border-foreground py-1">
                    *** QUITAÇÃO TOTAL ***
                  </p>
                )}
                <p className="border-t border-dashed border-border my-2" />
                <p>Operador: {receiptData.operator_name}</p>
                <p className="text-center mt-2 text-muted-foreground">
                  Obrigado pelo pagamento!
                </p>
              </div>
            </GlassCard>
          )}
        </>
      )}
    </div>
  );
}


// ─── TAB 3: VENCIDOS ─────────────────────────────────────────────────

interface VencidosTabProps {
  onReceivePayment: (customerId: string) => void;
}

function VencidosTab({ onReceivePayment }: VencidosTabProps) {
  const { tenant } = useAuth();
  const [overdueDebts, setOverdueDebts] = useState<Debt[]>([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ total_amount: 0, total_customers: 0 });

  useEffect(() => {
    fetchOverdue();
  }, []);

  const fetchOverdue = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/credit/debts/overdue');
      if (!res.ok) throw new Error('Erro ao carregar vencidos');
      const data: OverdueResponse = await res.json();
      setOverdueDebts(data.data);
      setSummary({
        total_amount: data.summary.total_amount,
        total_customers: data.summary.total_customers,
      });
    } catch {
      toast.error('Erro ao carregar débitos vencidos');
    } finally {
      setLoading(false);
    }
  };

  const buildWhatsAppLink = (debt: Debt) => {
    const phone = debt.customer_phone?.replace(/\D/g, '') || '';
    const phoneFormatted = phone.startsWith('55') ? phone : `55${phone}`;
    const message = encodeURIComponent(
      `Olá ${debt.customer_name}! Informamos que sua conta de ${formatCurrency(Number(debt.amount))} venceu em ${formatDate(debt.due_date)}. Por favor, regularize seu débito. Obrigado! - ${tenant?.name || 'Nosso Estabelecimento'}`
    );
    return `https://wa.me/${phoneFormatted}?text=${message}`;
  };

  const columns: Column<Debt>[] = [
    { key: 'customer_name', header: 'Cliente', sortable: true },
    {
      key: 'amount',
      header: 'Valor',
      sortable: true,
      render: (row) => <span className="font-medium">{formatCurrency(Number(row.amount))}</span>,
    },
    {
      key: 'due_date',
      header: 'Vencimento',
      sortable: true,
      render: (row) => formatDate(row.due_date),
    },
    {
      key: 'days_overdue',
      header: 'Dias em Atraso',
      render: (row) => {
        const days = row.days_overdue ?? calcDaysOverdue(row.due_date);
        return <span className="text-red-400 font-bold">{days} dias</span>;
      },
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => onReceivePayment(row.customer_id)}
            className="rounded-md bg-green-600/20 px-2 py-1 text-xs text-green-400 hover:bg-green-600/30 transition-colors"
          >
            <DollarSign className="inline h-3 w-3 mr-1" />
            Receber
          </button>
          {row.customer_phone && (
            <a
              href={buildWhatsAppLink(row)}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md bg-green-600/20 px-2 py-1 text-xs text-green-400 hover:bg-green-600/30 transition-colors"
            >
              <MessageCircle className="inline h-3 w-3 mr-1" />
              WhatsApp
            </a>
          )}
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-red-500/20 p-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Vencido</p>
              <p className="text-lg font-bold text-red-400">
                {formatCurrency(summary.total_amount)}
              </p>
            </div>
          </div>
        </GlassCard>
        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-orange-500/20 p-2">
              <Users className="h-5 w-5 text-orange-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Clientes com Atraso</p>
              <p className="text-lg font-bold text-foreground">{summary.total_customers}</p>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={overdueDebts}
        getRowKey={(row) => row.id}
        emptyMessage="Nenhum débito vencido encontrado"
      />
    </div>
  );
}

// ─── TAB 4: HISTÓRICO DO CLIENTE ─────────────────────────────────────

function HistoricoTab() {
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [creditData, setCreditData] = useState<CustomerCreditResponse | null>(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerSearch.length >= 2) {
        searchCustomers(customerSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  const searchCustomers = async (query: string) => {
    if (query.length < 2) {
      setCustomers([]);
      return;
    }
    setLoadingCustomers(true);
    try {
      const res = await apiFetch(`/api/customers?search=${encodeURIComponent(query)}`);
      if (!res.ok) throw new Error('Erro ao buscar clientes');
      const data = await res.json();
      setCustomers(data.data || data || []);
    } catch {
      toast.error('Erro ao buscar clientes');
    } finally {
      setLoadingCustomers(false);
    }
  };

  const loadHistory = async (customerId: string) => {
    setLoadingHistory(true);
    try {
      const res = await apiFetch(`/api/credit/customer/${customerId}`);
      if (!res.ok) throw new Error('Erro ao carregar histórico');
      const data: CustomerCreditResponse = await res.json();
      setSelectedCustomer(data.customer);
      setCreditData(data);
    } catch {
      toast.error('Erro ao carregar histórico do cliente');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSelectCustomer = (customer: Customer) => {
    setCustomers([]);
    setCustomerSearch('');
    loadHistory(customer.id);
  };

  return (
    <div className="space-y-6">
      {/* Customer search */}
      {!selectedCustomer && (
        <GlassCard>
          <h3 className="text-sm font-semibold text-foreground mb-4">Buscar Cliente</h3>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar por nome ou CPF..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              className="w-full rounded-lg border border-border bg-background pl-9 pr-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground"
            />
            {loadingCustomers && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>
          {customers.length > 0 && (
            <div className="mt-2 rounded-lg border border-border bg-card max-h-48 overflow-y-auto">
              {customers.map((c) => (
                <button
                  key={c.id}
                  onClick={() => handleSelectCustomer(c)}
                  className="w-full text-left px-4 py-2.5 hover:bg-accent transition-colors border-b border-border/50 last:border-0"
                >
                  <span className="text-sm font-medium text-foreground">{c.full_name}</span>
                  {c.cpf && (
                    <span className="ml-2 text-xs text-muted-foreground">CPF: {c.cpf}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </GlassCard>
      )}

      {loadingHistory && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      )}

      {selectedCustomer && creditData && !loadingHistory && (
        <>
          {/* Customer header */}
          <GlassCard>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground">
                Histórico - {selectedCustomer.full_name}
              </h3>
              <button
                onClick={() => {
                  setSelectedCustomer(null);
                  setCreditData(null);
                }}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Trocar cliente
              </button>
            </div>

            {/* Credit summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Limite</p>
                <p className="text-sm font-bold text-foreground">
                  {selectedCustomer.credit_limit !== null
                    ? formatCurrency(selectedCustomer.credit_limit)
                    : 'Ilimitado'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Saldo Devedor</p>
                <p className="text-sm font-bold text-red-400">
                  {formatCurrency(selectedCustomer.outstanding_balance)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Débitos Abertos</p>
                <p className="text-sm font-bold text-foreground">{creditData.summary.total_debts}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Vencidos</p>
                <p className="text-sm font-bold text-red-400">{creditData.summary.overdue_count}</p>
              </div>
            </div>
          </GlassCard>

          {/* Debts timeline */}
          <GlassCard>
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <History className="h-4 w-4 text-primary" />
              Débitos
            </h3>
            {creditData.debts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                Nenhum débito em aberto.
              </p>
            ) : (
              <div className="space-y-3">
                {creditData.debts.map((debt) => (
                  <div
                    key={debt.id}
                    className="flex items-center justify-between rounded-lg border border-border/50 px-4 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-2 w-2 rounded-full ${
                          debt.status === 'pago'
                            ? 'bg-gray-400'
                            : debt.status === 'vencido'
                              ? 'bg-red-400'
                              : 'bg-green-400'
                        }`}
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {formatCurrency(Number(debt.amount))}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Compra: {formatDate(debt.purchase_date)} | Venc:{' '}
                          {formatDate(debt.due_date)}
                        </p>
                      </div>
                    </div>
                    <Badge
                      variant={
                        debt.status === 'pago'
                          ? 'muted'
                          : debt.status === 'vencido'
                            ? 'destructive'
                            : 'success'
                      }
                      dot
                    >
                      {debt.status === 'pago'
                        ? 'Pago'
                        : debt.status === 'vencido'
                          ? 'Vencido'
                          : 'Em dia'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </GlassCard>

          {/* Overdue debts */}
          {creditData.overdue_debts.length > 0 && (
            <GlassCard>
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-400" />
                Débitos Vencidos ({creditData.overdue_debts.length})
              </h3>
              <div className="space-y-2">
                {creditData.overdue_debts.map((debt) => (
                  <div
                    key={debt.id}
                    className="flex items-center justify-between rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-3"
                  >
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {formatCurrency(Number(debt.amount))}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Venceu em {formatDate(debt.due_date)} ({calcDaysOverdue(debt.due_date)} dias
                        atrás)
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>
          )}

          {/* Pre-overdue debts */}
          {creditData.pre_overdue_debts.length > 0 && (
            <GlassCard>
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Clock className="h-4 w-4 text-yellow-400" />
                Próximos a Vencer ({creditData.pre_overdue_debts.length})
              </h3>
              <div className="space-y-2">
                {creditData.pre_overdue_debts.map((debt) => (
                  <div
                    key={debt.id}
                    className="flex items-center justify-between rounded-lg border border-yellow-500/30 bg-yellow-500/5 px-4 py-3"
                  >
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {formatCurrency(Number(debt.amount))}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Vence em {formatDate(debt.due_date)}
                      </p>
                    </div>
                    <Badge variant="warning" dot>
                      Próximo
                    </Badge>
                  </div>
                ))}
              </div>
            </GlassCard>
          )}
        </>
      )}
    </div>
  );
}


// ─── MAIN PAGE ───────────────────────────────────────────────────────

const tabs: { id: CreditTab; label: string; icon: React.ReactNode }[] = [
  { id: 'debitos', label: 'Débitos', icon: <CreditCard className="h-4 w-4" /> },
  { id: 'receber', label: 'Receber Pagamento', icon: <DollarSign className="h-4 w-4" /> },
  { id: 'vencidos', label: 'Vencidos', icon: <AlertTriangle className="h-4 w-4" /> },
  { id: 'historico', label: 'Histórico do Cliente', icon: <History className="h-4 w-4" /> },
];

export function CreditPage() {
  const [activeTab, setActiveTab] = useState<CreditTab>('debitos');
  const [preSelectedCustomerId, setPreSelectedCustomerId] = useState<string | undefined>();

  const handleReceivePayment = (customerId: string) => {
    setPreSelectedCustomerId(customerId);
    setActiveTab('receber');
  };

  // Reset pre-selected customer when switching away from receber tab
  useEffect(() => {
    if (activeTab !== 'receber') {
      setPreSelectedCustomerId(undefined);
    }
  }, [activeTab]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Crédito</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg bg-card p-1 border border-border overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-primary text-white shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'debitos' && <DebitosTab />}
      {activeTab === 'receber' && (
        <ReceberPagamentoTab preSelectedCustomerId={preSelectedCustomerId} />
      )}
      {activeTab === 'vencidos' && <VencidosTab onReceivePayment={handleReceivePayment} />}
      {activeTab === 'historico' && <HistoricoTab />}
    </div>
  );
}
